import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Briefcase, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";

export const Recruit = () => {
  const r = useContent().recruit;
  const [selected, setSelected] = useState(null);
  if (!r) return null;

  return (
    <section id="recruit" data-testid="recruit-section" className="bg-ink py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-6">{r.eyebrow}</p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-jp text-4xl md:text-6xl font-bold tracking-tight chrome-text leading-[1.15] whitespace-pre-line">{r.heading}</h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/60 whitespace-pre-line">{r.intro}</p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          {(r.positions || []).map((p, i) => (
            <Reveal key={p.id || i} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => setSelected(p)}
                className="hud group relative h-full w-full text-left rounded-2xl border border-white/10 bg-black/40 p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none"
                data-testid={`recruit-position-${i}`}
              >
                <span className="hud-tl" />
                <span className="hud-br" />
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-jp text-2xl font-bold tracking-tight text-white leading-tight group-hover:text-gold transition-colors duration-300">{p.title}</h3>
                  <ArrowUpRight size={20} className="shrink-0 mt-1 text-white/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.type && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-gold border border-gold/30 rounded-full px-3 py-1">
                      <Briefcase size={12} />{p.type}
                    </span>
                  )}
                  {p.location && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/60 border border-white/15 rounded-full px-3 py-1">
                      <MapPin size={12} />{p.location}
                    </span>
                  )}
                </div>
                <div className="hairline my-5" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 group-hover:text-gold/70 transition-colors duration-300">詳細を見る</span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            data-testid="recruit-detail-modal"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md p-6"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="hud relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-gold/30 bg-surface glow-cyan"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <button
                onClick={() => setSelected(null)}
                data-testid="recruit-detail-close"
                className="absolute top-5 right-5 z-10 grid place-items-center h-9 w-9 rounded-full bg-black/50 text-white/70 hover:text-gold transition-colors"
              >
                <X size={20} />
              </button>
              <div className="p-8 md:p-10">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mb-4">Recruit / Position</p>
                <div className="flex items-center gap-3 flex-wrap mb-4">
                  {selected.type && (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/30 rounded-full px-3 py-1">
                      <Briefcase size={11} />{selected.type}
                    </span>
                  )}
                  {selected.location && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/50"><MapPin size={13} />{selected.location}</span>
                  )}
                </div>
                <h3 className="font-jp text-3xl md:text-4xl font-bold tracking-tight chrome-text leading-tight">{selected.title}</h3>
                <div className="hairline my-7" />
                <p className="font-jp text-base font-light leading-loose text-white/70 whitespace-pre-line">{selected.body}</p>
                <Link
                  to={`/apply?position=${encodeURIComponent(selected.title)}`}
                  data-testid="recruit-detail-apply"
                  className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors duration-300"
                >
                  {r.cta_label}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
